import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mail, Phone, MessageCircle } from "lucide-react";

const INITIAL = {
  full_name: "", email: "", phone: "", whatsapp: "", country: "",
  preferred_destination: "", preferred_program: "", group_size: "", message: "",
};

const Input = ({ label, name, value, onChange, type = "text", required, testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required}
      data-testid={testId}
      className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm outline-none transition-colors"
    />
  </label>
);

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("teachmeonlineca@gmail.com");
  const [phone, setPhone] = useState("+1 825 365 8805");

  useEffect(() => {
    api.get("/site-config").then(({ data }) => {
      setWhatsapp(data.whatsapp_number || "");
      if (data.contact_email) setEmail(data.contact_email);
      if (data.contact_phone) setPhone(data.contact_phone);
    });
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact-inquiries", form);
      toast.success("Thanks! Your message has been received.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16" data-testid="contact-page">
      <div className="max-w-3xl mb-14">
        <p className="text-sm font-bold text-teal-600 uppercase tracking-widest mb-3">Contact</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]">
          Start Your Global <span className="text-orange-500">Learning Journey</span>
        </h1>
        <p className="mt-5 text-slate-600">Have a question or want a customized package for your group? Send us a message and we'll get back to you.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          <a href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 p-5 rounded-2xl bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-[#25D366] grid place-items-center text-white">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">WhatsApp</p>
              <p className="font-semibold text-slate-900">{phone}</p>
            </div>
          </a>
          <a href={`mailto:${email}`} data-testid="contact-email-link" className="flex items-center gap-3 p-5 rounded-2xl bg-red-50 border border-red-100 hover:bg-red-100 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-[#D52B1E] grid place-items-center text-white">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-bold text-red-800 uppercase tracking-wider">Email</p>
              <p className="font-semibold text-slate-900 break-all">{email}</p>
            </div>
          </a>
          <a href={`tel:${phone.replace(/\s+/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-slate-900 grid place-items-center text-white">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Phone</p>
              <p className="font-semibold text-slate-900">{phone}</p>
            </div>
          </a>
        </div>

        <form onSubmit={submit} className="lg:col-span-2 rounded-3xl bg-white border border-slate-100 shadow-lg shadow-blue-500/5 p-6 md:p-8 grid gap-4" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required testId="contact-fullname" />
            <Input label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Phone" name="phone" value={form.phone} onChange={onChange} testId="contact-phone" />
            <Input label="WhatsApp" name="whatsapp" value={form.whatsapp} onChange={onChange} testId="contact-whatsapp" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Country" name="country" value={form.country} onChange={onChange} testId="contact-country" />
            <Input label="Group Size" name="group_size" value={form.group_size} onChange={onChange} testId="contact-groupsize" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Preferred Destination" name="preferred_destination" value={form.preferred_destination} onChange={onChange} testId="contact-pref-dest" />
            <Input label="Preferred Program" name="preferred_program" value={form.preferred_program} onChange={onChange} testId="contact-pref-prog" />
          </div>
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Message</span>
            <textarea name="message" value={form.message} onChange={onChange} rows={4} data-testid="contact-message"
              className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm outline-none resize-none transition-colors" />
          </label>
          <div className="flex flex-wrap gap-3 mt-2">
            <button type="submit" disabled={submitting} data-testid="contact-submit-request"
              className="px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold text-sm shadow-lg shadow-orange-500/30 transition-colors">
              {submitting ? "Submitting…" : "Request Information"}
            </button>
            <a href="/packages" data-testid="contact-get-packages" className="px-6 py-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold text-sm transition-colors">
              Get Package Details
            </a>
            <a href="/partner" data-testid="contact-partner-cta" className="px-6 py-3 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-900 font-semibold text-sm transition-colors">
              Partner With Us
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
